import { Link, useHistory, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import * as localService from "../../../services/localService";
import logo from "../../../assets/img/spacia/web/spacia_logo.png";
import bg from "../../../assets/img/spacia/web/Background.png";

import { PhoneInput, isPhoneValid } from "../../../components/ui/phone-input";
import logger from "../../../utils/logger";
import { organizationApiService, apiV2Service, v2PublicApiService } from "../../../services/apiV2Service";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { showApiMessage } from "../../../utils/messageHandler";
import { motion } from "framer-motion";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { getWebOriginForFlavor } from "../../../utils/deepLink";

// Utility functions
const hasText = text => {
  return text !== null && text !== undefined && text.trim().length > 0;
};

const isAlphanumeric = text => {
  return /^[a-zA-Z0-9]+$/.test(text);
};

const extractDomain = url => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return "";
  }
};

const formatPhoneToInternational = (phone, callingCode) => {
  // Remove any non-digit characters
  const cleaned = phone.replace(/\D/g, "");

  // If it already starts with the calling code, return as is
  if (cleaned.startsWith(callingCode.replace("+", ""))) {
    return `+${cleaned}`;
  }

  // Otherwise, prepend the calling code
  return `+${callingCode.replace("+", "")}${cleaned}`;
};

// Account types offered in host-only mode (/partner-signup)
const HOST_ORG_TYPE_CODES = ["HOST_PROPERTY_DISTRIBUTION", "HOST_PROPERTY_MANAGEMENT"];

const PackageSignup = ({ hostOnly = false }) => {
  const history = useHistory();
  const location = useLocation();

  // Preserve the page the visitor came from (e.g. a space they were choosing
  // dates for) so signing in later still returns them there. Only same-origin
  // paths are honoured, never an absolute URL from the query string.
  const redirectParam = new URLSearchParams(location.search || "").get("redirect");
  const safeRedirect = redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
    ? redirectParam
    : null;

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [isIndividualSubscriber, setIsIndividualSubscriber] = useState(false);

  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    // Set payment source when component mounts
    localStorage.setItem("paymentType", "package");
    localStorage.setItem("paymentSource", "package-signup");
  }, []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [organizationSize, setOrganizationSize] = useState("");

  // const [role, setRole] = useState('ROLE_SUBSCRIBER_OWNER');
  const [isLoading, setIsLoading] = useState(false);

  // Countries state
  const [countries, setCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  // New V2 fields
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCountryObject, setSelectedCountryObject] = useState(null);
  const [organizationCode, setOrganizationCode] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  // isLoadingCountries is now provided by useCountries hook
  const [orgTypeId, setOrgTypeId] = useState(4); // Default to Organization Account (Corporate Customer)
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [maxUsers, setMaxUsers] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Organization types state
  const [organizationTypes, setOrganizationTypes] = useState([]);
  const [isLoadingOrgTypes, setIsLoadingOrgTypes] = useState(false);

  // Validation state variables
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameError, setUsernameError] = useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [organizationCodeAvailable, setOrganizationCodeAvailable] = useState(null);
  const [organizationNameAvailable, setOrganizationNameAvailable] = useState(null);
  const [organizationCodeError, setOrganizationCodeError] = useState("");
  const [organizationNameError, setOrganizationNameError] = useState("");
  const [isCheckingOrganization, setIsCheckingOrganization] = useState(false);

  // Form validation state
  const [isEmptyFirstName, setIsEmptyFirstName] = useState(false);
  const [isEmptyLastName, setIsEmptyLastName] = useState(false);
  const [isEmptyUsername, setIsEmptyUsername] = useState(false);
  const [isEmptyEmail, setIsEmptyEmail] = useState(false);
  const [isEmptyCountry, setIsEmptyCountry] = useState(false);
  const [isEmptyOrganizationName, setIsEmptyOrganizationName] = useState(false);
  const [isEmptyOrganizationSize, setIsEmptyOrganizationSize] = useState(false);
  const [isEmptyOrganizationCode, setIsEmptyOrganizationCode] = useState(false);
  const [isEmptyAddress, setIsEmptyAddress] = useState(false);
  const [isEmptyCity, setIsEmptyCity] = useState(false);
  const [isEmptyRegion, setIsEmptyRegion] = useState(false);
  const [isEmptyLogo, setIsEmptyLogo] = useState(false);
  const [isEmptyWebsiteUrl, setIsEmptyWebsiteUrl] = useState(false);
  const [isEmptyMaxUsers, setIsEmptyMaxUsers] = useState(false);
  const [isInvalidPhoneNumber, setIsInvalidPhoneNumber] = useState(false);
  const [phoneNumberValidationError, setPhoneNumberValidationError] = useState("");

  // Username availability checking with debounce
  const checkUsernameAvailability = async usernameToCheck => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    setIsCheckingUsername(true);
    setUsernameError("");

    try {
      const response = await apiV2Service.checkUsernameAvailability(usernameToCheck);
      const exists = response.data?.exists || false;

      if (exists) {
        setUsernameAvailable(false);
        setUsernameError("Username is already taken");
      } else {
        setUsernameAvailable(true);
        setUsernameError(""); // Clear any previous error
      }
    } catch (error) {
      logger.error("Username availability check error:", error);
      setUsernameAvailable(null);
      setUsernameError("Username is unavailable");
    } finally {
      setIsCheckingUsername(false);
    }
  };

  // Organization validation checking with debounce
  const checkOrganizationAvailability = async (codeToCheck, nameToCheck) => {
    // At least one field must be provided
    if (!codeToCheck && !nameToCheck) {
      setOrganizationCodeAvailable(null);
      setOrganizationNameAvailable(null);
      setOrganizationCodeError("");
      setOrganizationNameError("");
      return;
    }

    setIsCheckingOrganization(true);
    setOrganizationCodeError("");
    setOrganizationNameError("");

    try {
      const response = await v2PublicApiService.checkOrganizationExists({
        code: codeToCheck || undefined,
        name: nameToCheck || undefined,
      });

      const { codeExists, nameExists } = response.data;

      // Handle code validation
      if (codeToCheck) {
        if (codeExists === true) {
          setOrganizationCodeAvailable(false);
          setOrganizationCodeError("Organization code is already taken");
        } else if (codeExists === false) {
          setOrganizationCodeAvailable(true);
          setOrganizationCodeError("");
        } else {
          setOrganizationCodeAvailable(null);
          setOrganizationCodeError("");
        }
      }

      // Handle name validation
      if (nameToCheck) {
        if (nameExists === true) {
          setOrganizationNameAvailable(false);
          setOrganizationNameError("Organization name is already taken");
        } else if (nameExists === false) {
          setOrganizationNameAvailable(true);
          setOrganizationNameError("");
        } else {
          setOrganizationNameAvailable(null);
          setOrganizationNameError("");
        }
      }
    } catch (error) {
      logger.error("Organization availability check error:", error);
      setOrganizationCodeAvailable(null);
      setOrganizationNameAvailable(null);
      setOrganizationCodeError("Unable to check organization availability");
      setOrganizationNameError("Unable to check organization availability");
    } finally {
      setIsCheckingOrganization(false);
    }
  };

  // Alphanumeric validation for organization code
  const isAlphanumeric = str => {
    return /^[a-zA-Z0-9]+$/.test(str);
  };

  // Debounced username check
  useEffect(() => {
    if (!username) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    // Check for leading/trailing spaces
    if (username.trim() !== username) {
      setUsernameAvailable(false);
      setUsernameError("Username cannot have leading or trailing spaces");
      return;
    }

    const timeoutId = setTimeout(() => {
      checkUsernameAvailability(username);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [username]);

  // Debounced organization validation check
  useEffect(() => {
    if (!organizationCode && !organizationName) {
      setOrganizationCodeAvailable(null);
      setOrganizationNameAvailable(null);
      setOrganizationCodeError("");
      setOrganizationNameError("");
      return;
    }

    const timeoutId = setTimeout(() => {
      checkOrganizationAvailability(organizationCode, organizationName);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [organizationCode, organizationName]);

  // Handle account type selection
  const handleAccountTypeSelection = accountType => {
    setSelectedAccountType(accountType);
    const isIndividual = accountType.code.includes("SUBSCRIBER_INDIVIDUAL");
    setIsIndividualSubscriber(isIndividual);

    if (!isIndividual) {
      // Set the org type for organization accounts
      setOrgTypeId(accountType.id);
    }

    setCurrentStep(2);
  };

  // Handle back to step 1
  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setSelectedAccountType(null);
    setIsIndividualSubscriber(false);
  };

  // Get display name for account type (matches selection display)
  const getAccountTypeDisplayName = accountType => {
    if (!accountType) return "";
    if (accountType.code.includes("SUBSCRIBER_INDIVIDUAL")) {
      return "Individual Member";
    }
    if (accountType.code === "CORPORATE_CUSTOMER") {
      return "Organization Account";
    }
    if (accountType.code === "HOST_PROPERTY_DISTRIBUTION") {
      return "Listing Partner";
    }
    if (accountType.code === "HOST_PROPERTY_MANAGEMENT") {
      return "Property Management Suite";
    }
    return accountType.name;
  };

  // Auto-generation removed - user must manually enter organization code
  // useEffect(() => {
  //   if (firstName && lastName) {
  //     const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  //     // Ensure max length of 10 characters
  //     const codeBase = initials.length <= 10 ? initials : initials.substring(0, 10);
  //     setOrganizationCode(codeBase);
  //   }
  // }, [firstName, lastName]);

  // Format phone number to international format
  const formatPhoneToInternational = (phoneNumber, countryCallingCode) => {
    if (!phoneNumber || !countryCallingCode) return phoneNumber;

    // Remove any existing country code and formatting
    let cleanNumber = phoneNumber.replace(/\D/g, "");

    // Remove leading country code if it exists
    const codeWithoutPlus = countryCallingCode.replace("+", "");
    if (cleanNumber.startsWith(codeWithoutPlus)) {
      cleanNumber = cleanNumber.substring(codeWithoutPlus.length);
    }

    // Remove leading zero if present (common in local formats)
    if (cleanNumber.startsWith("0")) {
      cleanNumber = cleanNumber.substring(1);
    }

    // Return in international format
    return codeWithoutPlus + cleanNumber;
  };

  // Extract domain from website URL
  const extractDomain = url => {
    try {
      if (!url) return "";
      let cleanUrl = url;
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        cleanUrl = "https://" + url;
      }
      const domain = new URL(cleanUrl).hostname;
      return domain.replace("www.", "");
    } catch {
      return url.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0];
    }
  };

  const loadCountries = async () => {
    setIsLoadingCountries(true);
    try {
      const response = await v2PublicApiService.getCountries();
      const countriesData = response?.data?.data || [];
      setCountries(countriesData);
    } catch (error) {
      logger.error("Failed to load countries:", error);
      showApiMessage(
        {
          response: {
            status: 500,
            data: {
              message: "Failed to load countries. Please refresh the page.",
            },
          },
        },
        "load countries"
      );
      setCountries([]);
    } finally {
      setIsLoadingCountries(false);
    }
  };

  const loadOrganizationTypes = async () => {
    setIsLoadingOrgTypes(true);
    try {
      const response = await v2PublicApiService.getOrganizationTypes();
      const orgTypesData = response?.data?.data || [];

      // Add Individual Member option manually if it doesn't exist
      const hasIndividualMember = orgTypesData.some(type => type.code.includes("SUBSCRIBER_INDIVIDUAL"));

      // Filter out HOST_CORPORATE_CUSTOMER from the list
      let finalOrgTypes = orgTypesData.filter(type => type.code !== "HOST_CORPORATE_CUSTOMER");

      // Partner signup only offers the two host account types
      if (hostOnly) {
        finalOrgTypes = finalOrgTypes.filter(type => HOST_ORG_TYPE_CODES.includes(type.code));
        setOrganizationTypes(finalOrgTypes);
        if (finalOrgTypes.length > 0) {
          setOrgTypeId(finalOrgTypes[0].id);
        }
        return;
      }

      if (!hasIndividualMember) {
        // Add Individual Member option
        const individualMemberType = {
          id: -1, // Temporary ID since this won't be sent to the corporate signup API
          name: "Individual Member",
          code: "SUBSCRIBER_INDIVIDUAL",
          admin: false,
          active: true,
          subscriptionType: "USER",
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
        };
        finalOrgTypes = [individualMemberType, ...finalOrgTypes];
      }

      setOrganizationTypes(finalOrgTypes);

      // Set Organization Account (Corporate Customer) as default if available
      const organizationAccount = finalOrgTypes.find(type => type.code === "CORPORATE_CUSTOMER");
      if (organizationAccount) {
        setOrgTypeId(organizationAccount.id);
      } else if (finalOrgTypes.length > 0) {
        // Fallback to first available organization type
        setOrgTypeId(finalOrgTypes[0].id);
      }
    } catch (error) {
      logger.error("Failed to load organization types:", error);
      showApiMessage(
        {
          response: {
            status: 500,
            data: {
              message: "Failed to load organization types. Please refresh the page.",
            },
          },
        },
        "load organization types"
      );
      setOrganizationTypes([]); // Ensure organizationTypes is always an array
    } finally {
      setIsLoadingOrgTypes(false);
    }
  };

  useEffect(() => {
    loadOrganizationTypes();
    loadCountries();
  }, []);

  // Individual subscriber validation function
  const areIndividualFieldsOkay = () => {
    let ok = true;
    if (!hasText(firstName)) {
      setIsEmptyFirstName(true);
      ok = false;
    }
    if (!hasText(lastName)) {
      setIsEmptyLastName(true);
      ok = false;
    }
    if (!hasText(username)) {
      setIsEmptyUsername(true);
      ok = false;
    }
    if (!hasText(email)) {
      setIsEmptyEmail(true);
      ok = false;
    }

    if (!mobile) {
      setIsInvalidPhoneNumber(true);
      setPhoneNumberValidationError("Please enter mobile number");
      ok = false;
    } else if (!isPhoneValid(mobile)) {
      setIsInvalidPhoneNumber(true);
      setPhoneNumberValidationError("Please enter a valid phone number for your selected country");
      ok = false;
    } else {
      setIsInvalidPhoneNumber(false);
    }

    // Check username availability
    if (usernameAvailable !== true) {
      if (username.trim() !== username) {
        setUsernameError("Username cannot have leading or trailing spaces");
      }
      ok = false;
    }

    return ok;
  };

  // Individual subscriber signup function
  const submitIndividualSubscriberSignup = async e => {
    e.preventDefault();

    // Validate fields before proceeding
    if (!areIndividualFieldsOkay()) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Please correct the errors in the form." },
          },
        },
        "validate form"
      );
      return;
    }

    setIsLoading(true);

    try {
      // PhoneInput with international prop already returns the number in international format
      // No need for additional formatting
      const signupData = {
        username: username,
        firstName: firstName,
        lastName: lastName,
        emailAddress: email,
        msisdn: mobile, // Use mobile directly as it's already in international format
        confirmationUrl: `${window.location.origin}/?action=confirm_account`,
      };

      logger.log("Individual Subscriber Signup data:", signupData);

      const res = await apiV2Service.individualSubscriberSignup(signupData);
      if (res.status === 200 || res.status === 201) {
        // Don't show toast - redirect to success screen instead
        // showApiMessage(res, "individual subscriber signup");

        // Store some data for potential future use
        localStorage.setItem("pendingSignupEmail", email);

        // Redirect to success screen
        history.push("/signup-success");
      }
    } catch (err) {
      logger.error("Individual Subscriber Signup error:", err);
      showApiMessage(err, "signup");
    } finally {
      setIsLoading(false);
    }
  };

  // Remove password toggle functions - not needed

  const isLoggedIn = user => {
    return user != null && Object.keys(user).length !== 0;
  };

  const routeTo = path => {
    history.push(path);
  };

  const redirectBasedOnRole = () => {
    const currentUser = localService.getUser();
    let destination = "/startbooking";

    if (!isLoggedIn(currentUser)) {
      destination = "/signin";
    } else {
      const role = currentUser.role;

      switch (role) {
        case "ROLE_ADMINISTRATOR":
          destination = "/dashboard";
          break;

        case "ROLE_SUBSCRIBER_OWNER":
          destination = "/startbooking";
          break;

        default:
          destination = "/spaces-and-services";
      }
    }

    return destination;
  };

  function hasText(text) {
    return text != null && text !== "";
  }

  useEffect(() => {
    if (hasText(firstName)) {
      setIsEmptyFirstName(false);
    }
    if (hasText(lastName)) {
      setIsEmptyLastName(false);
    }
    if (hasText(username)) {
      setIsEmptyUsername(false);
    }
    if (hasText(email)) {
      setIsEmptyEmail(false);
    }
    if (mobile) {
      setIsInvalidPhoneNumber(false);
    }
    if (organizationName) {
      setIsEmptyOrganizationName(false);
    }
    if (organizationSize) {
      setIsEmptyOrganizationSize(false);
    }
    if (organizationCode) {
      setIsEmptyOrganizationCode(false);
    }
    if (address) {
      setIsEmptyAddress(false);
    }
    if (city) {
      setIsEmptyCity(false);
    }
    if (region) {
      setIsEmptyRegion(false);
    }
    if (logoFile || logoUrl) {
      setIsEmptyLogo(false);
    }
    if (websiteUrl) {
      setIsEmptyWebsiteUrl(false);
    }
    if (maxUsers) {
      setIsEmptyMaxUsers(false);
    }
  }, [
    firstName,
    lastName,
    username,
    email,
    mobile,
    organizationName,
    organizationSize,
    organizationCode,
    address,
    city,
    region,
    logoFile,
    websiteUrl,
    maxUsers,
  ]);

  const areFieldsOkay = () => {
    let ok = true;
    if (!hasText(firstName)) {
      setIsEmptyFirstName(true);
      ok = false;
    }
    if (!hasText(lastName)) {
      setIsEmptyLastName(true);
      ok = false;
    }
    if (!hasText(username)) {
      setIsEmptyUsername(true);
      ok = false;
    }
    if (!hasText(email)) {
      setIsEmptyEmail(true);
      ok = false;
    }
    if (!organizationName) {
      setIsEmptyOrganizationName(true);
      ok = false;
    }
    if (!maxUsers) {
      setIsEmptyMaxUsers(true);
      ok = false;
    }
    if (!selectedCountry) {
      setIsEmptyCountry(true);
      ok = false;
    }

    if (!mobile) {
      setIsInvalidPhoneNumber(true);
      setPhoneNumberValidationError("Please enter mobile number");
      ok = false;
    } else if (!isPhoneValid(mobile)) {
      setIsInvalidPhoneNumber(true);
      setPhoneNumberValidationError("Please enter a valid phone number for your selected country");
      ok = false;
    } else {
      setIsInvalidPhoneNumber(false);
    }

    // Check organization code validation only if provided
    if (organizationCode && !isAlphanumeric(organizationCode)) {
      setOrganizationCodeError("Organization code must contain only letters and numbers");
      ok = false;
    }

    if (organizationNameAvailable === false) {
      ok = false;
    }

    if (username && username.trim() !== username) {
      setUsernameAvailable(false);
      setUsernameError("Username cannot have leading or trailing spaces");
      ok = false;
    }

    return ok;
  };

  // Handle logo file selection
  const handleLogoFileChange = e => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type and size
      if (!file.type.startsWith("image/")) {
        showApiMessage(
          {
            response: {
              status: 400,
              data: { message: "Please select a valid image file" },
            },
          },
          "validate image"
        );
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        showApiMessage(
          {
            response: {
              status: 400,
              data: { message: "Image file size must be less than 5MB" },
            },
          },
          "validate image"
        );
        return;
      }
      setLogoFile(file);
      setLogoUrl(""); // Clear any existing URL
      setIsEmptyLogo(false);
    }
  };

  // Handle logo removal
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoUrl("");
    setIsEmptyLogo(false);
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Upload logo and get URL
  const uploadLogoIfNeeded = async () => {
    if (logoFile) {
      setUploadingLogo(true);
      try {
        const response = await spaceManagementService.uploadMedia([logoFile]);
        if ((response.status === 200 || response.status === 201) && response.data && response.data.length > 0) {
          return response.data[0]; // Return the uploaded image URL
        } else {
          throw new Error("Failed to upload logo");
        }
      } catch (error) {
        logger.error("Logo upload error:", error);
        throw new Error("Failed to upload logo image");
      } finally {
        setUploadingLogo(false);
      }
    }
    return logoUrl; // Return existing URL if no file to upload
  };

  const submitSignup = async e => {
    e.preventDefault();

    // Log all field values for debugging
    logger.log("📝 Form submission - Field values:", {
      firstName,
      lastName,
      username,
      email,
      mobile,
      organizationName,
      maxUsers,
      usernameAvailable,
      organizationNameAvailable,
    });

    // Validate fields before proceeding
    if (!areFieldsOkay()) {
      // Build specific error message
      const missingFields = [];
      if (!firstName) missingFields.push("First Name");
      if (!lastName) missingFields.push("Last Name");
      if (!username) missingFields.push("Username");
      if (!email) missingFields.push("Email");
      if (!mobile) missingFields.push("Phone Number");
      if (!organizationName) missingFields.push("Organization Name");
      if (!maxUsers) missingFields.push("Max Users");
      if (!selectedCountry) missingFields.push("Country");

      const errorMessage =
        missingFields.length > 0
          ? `Please fill in: ${missingFields.join(", ")}`
          : "Please correct the errors in the form.";

      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: errorMessage },
          },
        },
        "validate form"
      );
      return;
    }

    // Check username availability
    if (usernameAvailable !== true) {
      if (usernameError) {
        showApiMessage({ response: { status: 400, data: { message: usernameError } } }, "validate username");
      } else if (isCheckingUsername) {
        showApiMessage(
          {
            response: {
              status: 400,
              data: { message: "Please wait while we check username availability..." },
            },
          },
          "validate username"
        );
      } else {
        showApiMessage(
          {
            response: {
              status: 400,
              data: { message: "Please enter a valid and available username." },
            },
          },
          "validate username"
        );
      }
      return;
    }

    // Check organization name validation only (org code is optional)
    if (organizationNameAvailable === false) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Organization name is already taken" },
          },
        },
        "validate organization"
      );
      return;
    }

    // Check if organization name availability check is still pending
    if (isCheckingOrganization) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Please wait while we validate your organization details..." },
          },
        },
        "validate organization"
      );
      return;
    }

    setIsLoading(true);

    try {
      // Upload logo first if a file is selected
      let finalLogoUrl = logoUrl;
      if (logoFile) {
        finalLogoUrl = await uploadLogoIfNeeded();
      }

      // PhoneInput with international prop already returns the number in international format
      const formattedMsisdn = mobile;

      const signupData = {
        name: organizationName,
        code: null,
        typeId: orgTypeId,
        address: null,
        adminProfile: {
          username: username,
          lastName: lastName,
          firstName: firstName,
          msisdn: formattedMsisdn,
          emailAddress: email,
        },
        confirmationUrl: `${getWebOriginForFlavor(orgTypeId === 4 ? "corporate" : "individual")}/?action=confirm_account`,
        contactEmail: email,
        alternateEmail: "",
        contactMsisdn: formattedMsisdn,
        city: null,
        region: null,
        countryId: selectedCountry, // Country ID from dropdown
        logoUrl: null,
        domain: null,
        webUrl: null,
        spaceType: "LOW",
        projectId: null,
        groupId: null,
        config: {
          maxUsers: parseInt(maxUsers),
          enableMonthlyStatementAlerts: true,
        },
      };

      logger.log("Signup data:", signupData);

      const res = await organizationApiService.selfSignup(signupData);
      if (res.status === 200 || res.status === 201) {
        // Don't show toast - redirect to success screen instead
        // showApiMessage(res, "corporate signup");

        // Store some data for potential future use
        localStorage.setItem("maxUsers", maxUsers);
        localStorage.setItem("pendingSignupEmail", email);

        // Redirect to success screen
        history.push("/signup-success");
      }
    } catch (err) {
      logger.error("Signup error:", err);
      showApiMessage(err, "signup");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex relative items-start w-full min-h-screen">
      {/* Background Image */}
      <div
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{ backgroundImage: `url(${bg})` }}
      ></div>

      {/* Logo */}
      <div className="absolute top-8 left-8 z-10">
        <Link to="/home">
          <img src={logo} alt="Spacia Logo" className="w-auto h-24" />
        </Link>
      </div>

      {/* Content Card */}
      <div className="relative z-10 p-6 mx-auto mt-20 mb-20 mr-8 ml-auto w-full max-w-lg bg-white rounded-lg shadow-lg md:p-8 md:mt-32 md:mb-32 md:mr-24 lg:mr-32">
        <div className="w-full">
          <header className="mb-4 text-center">
            <h1 className="mb-2 text-lg font-semibold md:text-xl">
              {currentStep === 1
                ? hostOnly
                  ? "Choose your partner account type"
                  : "Choose your account type"
                : "Let's set you up!"}
            </h1>
            <p className="text-sm text-gray-700">
              {currentStep === 1 ? (
                hostOnly ? (
                  "Select how you want to list and manage your spaces on SPACIA"
                ) : (
                  "Select the type of account you want to create"
                )
              ) : selectedAccountType ? (
                <>
                  Complete the form to create your{" "}
                  <strong className="text-gray-900">{getAccountTypeDisplayName(selectedAccountType)}</strong> account
                </>
              ) : (
                "Complete the form below to create your account"
              )}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Already have an account?{" "}
              <span
                className="text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
                onClick={() => routeTo(safeRedirect ? `/signin?redirect=${encodeURIComponent(safeRedirect)}` : "/signin")}
              >
                Log in
              </span>
            </p>
            {currentStep === 2 && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleBackToStep1}
                  className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
                >
                  ← Back
                </button>
              </div>
            )}
          </header>

          {/* Animated Step Content */}
          {/* Step 1: Account Type Selection */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              className="space-y-4"
            >
              {isLoadingOrgTypes ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                  className="grid gap-4"
                >
                  {/* Shimmer loading cards */}
                  {[1, 2, 3].map(index => (
                    <div key={index} className="p-3 rounded-lg border border-gray-200 animate-pulse">
                      <div className="mb-2 w-3/4 h-5 bg-gray-200 rounded"></div>
                      <div className="w-full h-4 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </motion.div>
              ) : organizationTypes.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                  className="grid gap-2"
                >
                  {organizationTypes.map((orgType, index) => (
                    <motion.button
                      key={orgType.id}
                      type="button"
                      onClick={() => handleAccountTypeSelection(orgType)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.7,
                        delay: index * 0.15,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      whileHover={{
                        scale: 1.01,
                        borderColor: "#3b82f6",
                        transition: {
                          duration: 0.4,
                          ease: [0.25, 0.1, 0.25, 1],
                        },
                      }}
                      whileTap={{
                        scale: 0.99,
                        transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] },
                      }}
                      className="p-3 text-left rounded-lg border border-input hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all duration-300 group"
                    >
                      <h3 className="font-semibold text-sm text-gray-900 transition-colors duration-300 group-hover:text-blue-700">
                        {orgType.code.includes("SUBSCRIBER_INDIVIDUAL")
                          ? "Individual Member"
                          : orgType.code === "CORPORATE_CUSTOMER"
                            ? "Organization Account"
                            : orgType.code === "HOST_PROPERTY_DISTRIBUTION"
                              ? "Listing Partner"
                              : orgType.code === "HOST_PROPERTY_MANAGEMENT"
                                ? "Property Management Suite"
                                : orgType.name}
                      </h3>
                      <p className="mt-1 text-xs text-gray-600 leading-snug transition-colors duration-300">
                        {orgType.code.includes("SUBSCRIBER_INDIVIDUAL")
                          ? "Quick signup for individuals booking spaces, stays, or day passes."
                          : orgType.code === "CORPORATE_CUSTOMER"
                            ? "For startups, SMEs, or enterprises managing team passes and workspace bookings."
                            : orgType.code === "HOST_PROPERTY_DISTRIBUTION"
                              ? "For property or workspace owners listing their spaces on SPACIA's marketplace."
                              : orgType.code === "HOST_PROPERTY_MANAGEMENT"
                                ? "For organizations using SPACIA's SaaS platform to manage their own properties, bookings, and operations."
                                : "Full organization setup with admin privileges"}
                      </p>
                    </motion.button>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                  className="py-8 text-center"
                >
                  <p className="text-gray-500">No account types available. Please refresh the page.</p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Add space after account type selection */}
          {currentStep === 1 && <div className="mb-6"></div>}

          {/* Step 2: Form based on account type */}
          {currentStep === 2 && selectedAccountType && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {isIndividualSubscriber ? (
                <motion.form
                  onSubmit={submitIndividualSubscriberSignup}
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  {/* Individual Subscriber Form - Simple */}
                  <motion.div
                    className="form-group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <Label htmlFor="first-name">First name</Label>
                    <Input
                      id="first-name"
                      type="text"
                      autoComplete="off"
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      className={isEmptyFirstName ? "border-red-500" : ""}
                    />
                    {isEmptyFirstName && <div className="mt-1 text-sm text-red-500">This field can't be empty</div>}
                  </motion.div>

                  <div className="form-group">
                    <Label htmlFor="last-name">Last name</Label>
                    <Input
                      id="last-name"
                      type="text"
                      autoComplete="off"
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      className={isEmptyLastName ? "border-red-500" : ""}
                    />
                    {isEmptyLastName && <div className="mt-1 text-sm text-red-500">This field can't be empty</div>}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="username">Username</Label>
                    <div className="relative">
                      <Input
                        id="username"
                        type="text"
                        autoComplete="off"
                        placeholder="Enter your username"
                        value={username}
                        onChange={e => {
                          setUsername(e.target.value);
                          if (isEmptyUsername) setIsEmptyUsername(false);
                        }}
                        className={`pr-10 ${
                          isEmptyUsername || usernameError
                            ? "border-red-500"
                            : usernameAvailable === true
                              ? "border-green-500"
                              : ""
                        }`}
                      />
                      {/* Loading/Status indicator */}
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {isCheckingUsername ? (
                          <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : usernameAvailable === true ? (
                          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : usernameAvailable === false ? (
                          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : null}
                      </div>
                    </div>
                    {/* Username validation messages - show only one at a time */}
                    {isEmptyUsername ? (
                      <div className="mt-1 text-sm text-red-500">Username is required</div>
                    ) : usernameError ? (
                      <div className="mt-1 text-sm text-red-500">{usernameError}</div>
                    ) : usernameAvailable === true ? (
                      <div className="mt-1 text-sm text-green-600">Username is available!</div>
                    ) : null}
                    <p className="mt-1 text-xs text-gray-500">Username must be at least 3 characters long</p>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="off"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className={isEmptyEmail ? "border-red-500" : ""}
                    />
                    {isEmptyEmail && <div className="mt-1 text-sm text-red-500">Please enter your email</div>}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="phone">Phone number</Label>
                    <div
                      className={`relative ${isInvalidPhoneNumber ? "border border-red-500" : "border border-transparent"}`}
                    >
                      <PhoneInput
                        international
                        limitMaxLength={true}
                        defaultCountry="KE"
                        value={mobile}
                        onChange={value => setMobile(value || "")}
                        className="w-full bg-transparent rounded-md"
                      />
                    </div>
                    {isInvalidPhoneNumber && (
                      <div className="mt-1 text-sm text-red-500">
                        {phoneNumberValidationError || "Please enter a valid phone number"}
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <p className="mb-4 text-sm text-gray-800">
                      By creating an account, you agree to the{" "}
                      <a
                        href={process.env.PUBLIC_URL + "/assets/terms_conditions.pdf"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 underline font-medium border-b border-gray-900 pb-0.5"
                      >
                        terms & conditions
                      </a>{" "}
                      for the service.
                    </p>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, delay: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                      whileHover={{
                        scale: 1.02,
                        transition: {
                          duration: 0.4,
                          ease: [0.25, 0.1, 0.25, 1],
                        },
                      }}
                      whileTap={{
                        scale: 0.98,
                        transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] },
                      }}
                    >
                      <button
                        type="submit"
                        disabled={
                          isLoading ||
                          !firstName ||
                          !lastName ||
                          !username ||
                          !email ||
                          !mobile ||
                          usernameAvailable !== true
                        }
                        className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md text-white font-medium ${
                          isLoading ||
                          !firstName ||
                          !lastName ||
                          !username ||
                          !email ||
                          !mobile ||
                          usernameAvailable !== true
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        }`}
                      >
                        {isLoading ? (
                          <span className="flex items-center">
                            <svg
                              className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Creating Account...
                          </span>
                        ) : (
                          "Create Account"
                        )}
                      </button>
                    </motion.div>
                  </div>
                </motion.form>
              ) : (
                <motion.form
                  onSubmit={submitSignup}
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  {/* Corporate/Organization Form - Full */}

                  <div className="form-group">
                    <Label htmlFor="corp-first-name">First name</Label>
                    <Input
                      id="corp-first-name"
                      type="text"
                      autoComplete="off"
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      className={isEmptyFirstName ? "border-red-500" : ""}
                    />
                    {isEmptyFirstName && <div className="mt-1 text-sm text-red-500">This field can't be empty</div>}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="corp-last-name">Last name</Label>
                    <Input
                      id="corp-last-name"
                      type="text"
                      autoComplete="off"
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      className={isEmptyLastName ? "border-red-500" : ""}
                    />
                    {isEmptyLastName && <div className="mt-1 text-sm text-red-500">This field can't be empty</div>}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="corp-username">Username</Label>
                    <div className="relative">
                      <Input
                        id="corp-username"
                        type="text"
                        autoComplete="off"
                        placeholder="Enter your username"
                        value={username}
                        onChange={e => {
                          setUsername(e.target.value);
                          if (isEmptyUsername) setIsEmptyUsername(false);
                        }}
                        className={`pr-10 ${
                          isEmptyUsername || usernameError
                            ? "border-red-500"
                            : usernameAvailable === true
                              ? "border-green-500"
                              : ""
                        }`}
                      />
                      {/* Loading/Status indicator */}
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {isCheckingUsername ? (
                          <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : usernameAvailable === true ? (
                          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : usernameAvailable === false ? (
                          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : null}
                      </div>
                    </div>
                    {/* Username validation messages - show only one at a time */}
                    {isEmptyUsername ? (
                      <div className="mt-1 text-sm text-red-500">Username is required</div>
                    ) : usernameError ? (
                      <div className="mt-1 text-sm text-red-500">{usernameError}</div>
                    ) : usernameAvailable === true ? (
                      <div className="mt-1 text-sm text-green-600">Username is available!</div>
                    ) : null}
                    <p className="mt-1 text-xs text-gray-500">Username must be at least 3 characters long</p>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="org-name">Organization Name</Label>
                    <div className="relative">
                      <Input
                        id="org-name"
                        type="text"
                        autoComplete="off"
                        placeholder="Enter your Organization Name"
                        value={organizationName}
                        onChange={e => {
                          setOrganizationName(e.target.value);
                          if (isEmptyOrganizationName) setIsEmptyOrganizationName(false);
                        }}
                        className={`pr-10 ${
                          isEmptyOrganizationName || organizationNameError
                            ? "border-red-500"
                            : organizationNameAvailable === true
                              ? "border-green-500"
                              : ""
                        }`}
                      />
                      {/* Loading/Status indicator */}
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {isCheckingOrganization && organizationName ? (
                          <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : organizationNameAvailable === true ? (
                          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : organizationNameAvailable === false ? (
                          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : null}
                      </div>
                    </div>
                    {/* Organization name validation messages */}
                    {isEmptyOrganizationName ? (
                      <div className="mt-1 text-sm text-red-500">Organization name is required</div>
                    ) : organizationNameError ? (
                      <div className="mt-1 text-sm text-red-500">{organizationNameError}</div>
                    ) : organizationNameAvailable === true ? (
                      <div className="mt-1 text-sm text-green-600">Organization name is available!</div>
                    ) : null}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="off"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className={isEmptyEmail ? "border-red-500" : ""}
                    />
                    {isEmptyEmail && <div className="mt-1 text-sm text-red-500">Please enter your email</div>}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="phone">Phone number</Label>
                    <div>
                      <PhoneInput
                        defaultCountry="ke"
                        value={mobile}
                        onChange={value => setMobile(value || "")}
                        error={isInvalidPhoneNumber}
                      />
                    </div>
                    {isInvalidPhoneNumber && (
                      <div className="mt-1 text-sm text-red-500">
                        {phoneNumberValidationError || "Please enter a valid phone number"}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="country">Country *</Label>
                    <Select
                      value={selectedCountry ? selectedCountry.toString() : ""}
                      onValueChange={value => {
                        const countryId = parseInt(value);
                        setSelectedCountry(countryId);
                        const countryObj = countries.find(c => c.id === countryId);
                        setSelectedCountryObject(countryObj);
                        if (isEmptyCountry) setIsEmptyCountry(false);
                      }}
                    >
                      <SelectTrigger className={isEmptyCountry ? "border-red-500" : ""}>
                        <SelectValue
                          placeholder={isLoadingCountries ? "Loading countries..." : "Select your country"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map(country => (
                          <SelectItem key={country.id} value={country.id.toString()}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isEmptyCountry && <div className="mt-1 text-sm text-red-500">Please select your country</div>}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="max-users">Maximum Users in Organization *</Label>
                    <Input
                      id="max-users"
                      type="number"
                      min="5"
                      autoComplete="off"
                      placeholder="Maximum number of users in your organization"
                      value={maxUsers}
                      onChange={e => setMaxUsers(e.target.value)}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                      className={`pr-10 ${isEmptyMaxUsers ? "border-red-500" : ""}`}
                    />
                    {isEmptyMaxUsers && <div className="mt-1 text-sm text-red-500">This field is required</div>}
                    <p className="mt-1 text-xs text-gray-500">
                      Specify the maximum number of users that can be added to your organization
                    </p>
                  </div>

                  <div className="mt-6">
                    <p className="mb-4 text-sm text-gray-800">
                      By creating an account, you agree to the{" "}
                      <a
                        href={process.env.PUBLIC_URL + "/assets/terms_conditions.pdf"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 underline font-medium border-b border-gray-900 pb-0.5"
                      >
                        terms & conditions
                      </a>{" "}
                      for the service.
                    </p>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, delay: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
                      whileHover={{
                        scale: 1.02,
                        transition: {
                          duration: 0.4,
                          ease: [0.25, 0.1, 0.25, 1],
                        },
                      }}
                      whileTap={{
                        scale: 0.98,
                        transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] },
                      }}
                    >
                      <button
                        type="submit"
                        disabled={
                          isLoading ||
                          !firstName ||
                          !lastName ||
                          !username ||
                          !email ||
                          !mobile ||
                          !organizationName ||
                          !maxUsers ||
                          !selectedCountry ||
                          usernameAvailable !== true ||
                          organizationNameAvailable === false
                        }
                        className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md text-white font-medium ${
                          isLoading ||
                          !firstName ||
                          !lastName ||
                          !username ||
                          !email ||
                          !mobile ||
                          !organizationName ||
                          !maxUsers ||
                          !selectedCountry ||
                          usernameAvailable !== true ||
                          organizationNameAvailable === false
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        }`}
                      >
                        {isLoading ? (
                          <span className="flex items-center">
                            <svg
                              className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {uploadingLogo ? "Uploading Logo..." : "Creating Account..."}
                          </span>
                        ) : (
                          "Create Account"
                        )}
                      </button>
                    </motion.div>
                  </div>
                </motion.form>
              )}
            </motion.div>
          )}
        </div>
      </div>

      <style>{`
                .PhoneInput {
                    height: 42px;
                    display: flex;
                    align-items: center;
                    background-color: #F3F4F6;
                    border-radius: 0.375rem;
                    padding: 0 0.5rem;
                }

                .PhoneInput .PhoneInputInput {
                    border: none;
                    padding: 0.5rem;
                    height: 100%;
                    flex: 1;
                    outline: none;
                    background-color: transparent;
                }

                .PhoneInput .PhoneInputCountry {
                    padding-right: 0.5rem;
                    display: flex;
                    align-items: center;
                }
            `}</style>
    </div>
  );
};

export default PackageSignup;
